import React, {useState, useMemo, useCallback} from 'react';
import {Col, Dropdown, Row, Modal, Form} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button, Badge} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {ModalAlert, useAlert} from '@/components/alerts';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import RoleModal from './RoleModal';
import RoleDetailModal from './RoleDetailModal';
import RoleComparisonModal from './RoleComparisonModal';
import useData from '@/utils/useData';
import Spinner from '@/components/ui/Spinner';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import SearchBox from '@/components/search/SearchBox';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {buildURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {getAccessRoles} from '@/utils/roleCategories';
import {extractErrorMessage} from '@/utils/errorHandling';
import {isModuleEnabled} from '@/utils/modules';

interface Role {
    id: number;
    name: string;
    description?: string;
    user_count: number;
    permission_count: number;
    channel_access_count?: number;
    team_access_count?: number;
    restriction_profile_count?: number;
    created_on: string | null;
    updated_on: string | null;
    platform_managed?: boolean;
}

interface RoleCloneModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (cloneData: {name: string; description: string}) => Promise<void>;
    sourceRole: Role | null;
    isLoading: boolean;
}

const RoleCloneModal: React.FC<RoleCloneModalProps> = ({isOpen, onClose, onConfirm, sourceRole, isLoading}) => {
    const [formData, setFormData] = useState({name: '', description: ''});
    const [errors, setErrors] = useState<Record<string, string>>({});

    React.useEffect(() => {
        if (isOpen && sourceRole) {
            setFormData({
                name: `${sourceRole.name} ${i18n.t('roles_copy_suffix.text')}`,
                description: sourceRole.description
                    ? `${sourceRole.description} ${i18n.t('roles_cloned_from_with_desc.text', {roleName: sourceRole.name})}`
                    : i18n.t('roles_cloned_from.text', {roleName: sourceRole.name}),
            });
            setErrors({});
        }
    }, [isOpen, sourceRole]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = i18n.t('roles_name_required.text');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        await onConfirm(formData);
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({...prev, [field]: value}));
        if (errors[field]) {
            setErrors(prev => ({...prev, [field]: ''}));
        }
    };

    if (!sourceRole) {return null;}

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{i18n.t('roles_clone_role_title.text', {roleName: sourceRole.name})}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="mb-3">
                        <p className="text-muted">
                            {i18n.t('roles_clone_description.text', {
                                roleName: sourceRole.name,
                                permissionCount: sourceRole.permission_count
                            })}
                        </p>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{i18n.t('roles_new_role_name_required.text')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            isInvalid={!!errors.name}
                            disabled={isLoading}
                            placeholder={i18n.t('roles_enter_new_role_name.text')}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{i18n.t('roles_description.text')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            disabled={isLoading}
                            placeholder={i18n.t('roles_enter_role_description_optional.text')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {i18n.t('jt_cancel.text')}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={isLoading}
                        icon="mdi-content-copy"
                    >
                        {i18n.t('roles_clone_role.text')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const RolesListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {showSuccess, showError} = useAlert();

    const [roleModalOpen, setRoleModalOpen] = useState<boolean>(false);
    const [roleDetailModalOpen, setRoleDetailModalOpen] = useState<boolean>(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [roleToDelete, setRoleToDelete] = useState<{id: number; name: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [cloneModalOpen, setCloneModalOpen] = useState<boolean>(false);
    const [roleToClone, setRoleToClone] = useState<Role | null>(null);
    const [isCloning, setIsCloning] = useState<boolean>(false);
    const [comparisonModalOpen, setComparisonModalOpen] = useState<boolean>(false);
    const [preselectedRoleForComparison, setPreselectedRoleForComparison] = useState<Role | null>(null);

    const [rolesResponse, loadingRoles, storeAction] = useData(window.URLS.adminRolesList, {
        storeResolver: 'roles',
        actionResolver: (actions: any) => (payload: any) => {
            const rolesArray = Array.isArray(payload) ? payload : (payload?.roles || []);
            actions.store(rolesArray);
        },
        dataResolver: (state: any) => state.items,
        initialValue: [],
    });

    const allRolesFromAPI = Array.isArray(rolesResponse) ? rolesResponse : [];
    const allRoles = useMemo(() => getAccessRoles(allRolesFromAPI), [allRolesFromAPI]);

    const canViewRoles = hasPermission('fs.roles_permissions.role.list', userData?.permissions, true);
    const canCreateRoles = hasPermission('fs.roles_permissions.role.create', userData?.permissions, true);
    const canUpdateRoles = hasPermission('fs.roles_permissions.role.update', userData?.permissions, true);
    const canDeleteRoles = hasPermission('fs.roles_permissions.role.delete', userData?.permissions, true);
    const canViewRoleDetails = hasPermission('fs.roles_permissions.role.read', userData?.permissions, true);
    const canCloneRoles = hasPermission('fs.roles_permissions.role.clone', userData?.permissions, true);
    const canCompareRoles = hasPermission('fs.roles_permissions.role.compare', userData?.permissions, true);

    const deleteAction = useStoreActions((actions: any) => actions.roles?.deleteOne);
    const setReloadRoles = useStoreActions((actions: any) => actions.roles?.setReload);

    const filteredRoles = useMemo(() => {
        const rolesArray = Array.isArray(allRoles) ? allRoles : [];

        if (!searchQuery.trim()) {
            return rolesArray;
        }

        const query = searchQuery.toLowerCase();
        return rolesArray.filter((role: Role) =>
            role.name.toLowerCase().includes(query) ||
            (role.description && role.description.toLowerCase().includes(query))
        );
    }, [allRoles, searchQuery]);

    const openRoleModal = useCallback((role: Role | null = null): void => {
        setSelectedRole(role);
        setRoleModalOpen(true);
    }, []);

    const openRoleDetailModal = useCallback((role: Role): void => {
        setSelectedRole(role);
        setRoleDetailModalOpen(true);
    }, []);


    const reloadRoles = useCallback((): void => {
        if (setReloadRoles) {
            setReloadRoles(true);
        }
    }, [setReloadRoles]);

    const closeRoleModal = useCallback((): void => {
        setRoleModalOpen(false);
        setSelectedRole(null);
        reloadRoles();
    }, [reloadRoles]);

    const closeRoleDetailModal = useCallback((): void => {
        setRoleDetailModalOpen(false);
        setSelectedRole(null);
        reloadRoles();
    }, [reloadRoles]);

    const openDeleteConfirm = useCallback((id: number, name: string): void => {
        setRoleToDelete({id, name});
        setRoleToDelete({id, name});
        setDeleteConfirmOpen(true);
    }, []);

    const closeDeleteConfirm = useCallback((): void => {
        setDeleteConfirmOpen(false);
        setRoleToDelete(null);
        setIsDeleting(false);
    }, []);

    const confirmDelete = useCallback(async (): Promise<void> => {
        if (!roleToDelete) {return;}

        setIsDeleting(true);
        try {
            await axios.delete(buildURL('adminRoleDetail', {role_id: roleToDelete.id}));
            if (deleteAction) {
                deleteAction(roleToDelete.id);
            }
            showSuccess(i18n.t('roles_delete_success.text', {roleName: roleToDelete.name}));
            closeDeleteConfirm();
            reloadRoles();
        } catch (error: any) {
            console.error('Failed to delete role:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to delete role');
            showError(i18n.t('roles_delete_error.text', {error: errorMessage}));
            setIsDeleting(false);
        }
    }, [roleToDelete, deleteAction, showSuccess, showError, closeDeleteConfirm, reloadRoles]);

    const openCloneModal = useCallback((role: Role): void => {
        setRoleToClone(role);
        setCloneModalOpen(true);
    }, []);

    const closeCloneModal = useCallback((): void => {
        setCloneModalOpen(false);
        setRoleToClone(null);
        setIsCloning(false);
    }, []);

    const confirmClone = useCallback(async (cloneData: {name: string; description: string}): Promise<void> => {
        if (!roleToClone) {return;}

        setIsCloning(true);
        try {
            const response = await axios.post(buildURL('adminRoleClone', {role_id: roleToClone.id}), cloneData);
            showSuccess(i18n.t('roles_clone_success.text', {newRoleName: cloneData.name, sourceRoleName: roleToClone.name}));
            closeCloneModal();
            reloadRoles();
        } catch (error: any) {
            console.error('Failed to clone role:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to clone role');
            showError(i18n.t('roles_clone_error.text', {error: errorMessage}));
            setIsCloning(false);
        }
    }, [roleToClone, showSuccess, showError, closeCloneModal, reloadRoles]);

    const openComparisonModal = useCallback((preselectedRole: Role | null = null): void => {
        setPreselectedRoleForComparison(preselectedRole);
        setComparisonModalOpen(true);
    }, []);

    const closeComparisonModal = useCallback((): void => {
        setComparisonModalOpen(false);
        setPreselectedRoleForComparison(null);
    }, []);


    if (loadingUserData) {
        return <Spinner text={i18n.t('sp_loading.text')} centered fullHeight />;
    }

    if (!canViewRoles && !loadingUserData) {
        return (
            <>
                <ConditionalNav />
                <HelpTextBlock helpText={i18n.t('roles_help_text.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('roles_role_name.text'),
            accessorKey: 'name',
            cell: ({getValue, row}: {getValue: () => string; row: {original: Role}}) => {
                const name = getValue();
                const role = row.original;
                return (
                    <div className="d-flex align-items-center">
                        <span className="fw-bold">{name}</span>
                        {role.platform_managed && (
                            <Badge variant="secondary" className="ml-2">{i18n.t('roles_system_badge.text')}</Badge>
                        )}
                    </div>
                );
            },
        },
        {
            header: i18n.t('roles_description.text'),
            accessorKey: 'description',
            cell: ({getValue}: {getValue: () => string}) => {
                const desc = getValue();
                if (!desc) {return '-';}

                const MAX_DESCRIPTION_LENGTH = 200;

                if (desc.length > MAX_DESCRIPTION_LENGTH) {
                    const truncated = `${desc.substring(0, MAX_DESCRIPTION_LENGTH)}...`;
                    return (
                        <Tooltip content={desc} placement="top">
                            <span>{truncated}</span>
                        </Tooltip>
                    );
                }

                return desc;
            },
        },
        {
            header: i18n.t('roles_users.text'),
            accessorKey: 'user_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue();
                return (
                    <Badge variant="info">
                        {count} {count === 1 ? i18n.t('roles_user_singular.text') : i18n.t('roles_user_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('roles_permissions.text'),
            accessorKey: 'permission_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue();
                return (
                    <Badge variant="primary">
                        {count} {count === 1 ? i18n.t('roles_permission_singular.text') : i18n.t('roles_permission_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('roles_channel_access_header.text'),
            accessorKey: 'channel_access_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue() || 0;
                return (
                    <Badge variant="success">
                        {count} {count === 1 ? i18n.t('roles_channel_singular.text') : i18n.t('roles_channel_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('roles_team_access_header.text'),
            accessorKey: 'team_access_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue() || 0;
                return (
                    <Badge variant="warning">
                        {count} {count === 1 ? i18n.t('roles_team_singular.text') : i18n.t('roles_team_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('roles_restriction_profiles_header.text', {defaultValue: 'Restriction Profiles'}),
            accessorKey: 'restriction_profile_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue() || 0;
                return (
                    <Badge variant="secondary">
                        {count} {count === 1 ? i18n.t('restriction_profiles_profile_singular.text', {defaultValue: 'profile'}) : i18n.t('restriction_profiles_profile_plural.text', {defaultValue: 'profiles'})}
                    </Badge>
                );
            },
        },
        {
            id: 'lastModified',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (row: Role) => {
                if (!row.updated_on) {
                    return configLoaded && row.created_on ? formatDateTime(row.created_on, true, {setZone: false}) : '';
                }
                return configLoaded ? formatDateTime(row.updated_on, true, {setZone: false}) : '';
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: ({row}: {row: {original: Role}}) => {
                const role = row.original;
                const isPlatformManaged = role.platform_managed;
                const canEdit = canUpdateRoles && !isPlatformManaged;
                const canDelete = canDeleteRoles && !isPlatformManaged;
                const canView = canViewRoleDetails;
                const canClone = canCloneRoles;

                return (
                    <div className="d-inline-block">
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    id={`role-dropdown-${role.id}`}
                                    data-cy="role-list-outlineinfo-btn"
                                />

                                <Dropdown.Menu align="end">
                                    {canView && (
                                        <Dropdown.Item onClick={() => openRoleDetailModal(role)}>
                                            <Icon name="mdi-eye" className="mr-2" />
                                            {i18n.t('roles_view_details.text')}
                                        </Dropdown.Item>
                                    )}

                                    {canCompareRoles && (
                                        <Dropdown.Item onClick={() => openComparisonModal(role)}>
                                            <Icon name="mdi-compare" className="mr-2" />
                                            {i18n.t('roles_compare_with.text')}
                                        </Dropdown.Item>
                                    )}

                                    {canClone ? (
                                        <Dropdown.Item onClick={() => openCloneModal(role)}>
                                            <Icon name="mdi-content-copy" className="mr-2" />
                                            {i18n.t('roles_clone_role.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')} placement="top">
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-content-copy" className="mr-2" />
                                                    {i18n.t('roles_clone_role.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )}

                                    {canEdit ? (
                                        <Dropdown.Item onClick={() => openRoleModal(role)}>
                                            <Icon name="mdi-pencil" className="mr-2" />
                                            {i18n.t('jt_edit.text')}
                                        </Dropdown.Item>
                                    ) : isPlatformManaged ? (
                                        <Tooltip content={i18n.t('roles_system_roles_cannot_be_edited.text')} placement="top">
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-pencil" className="mr-2" />
                                                    {i18n.t('jt_edit.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')} placement="top">
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-pencil" className="mr-2" />
                                                    {i18n.t('jt_edit.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )}

                                    {canDelete && !isPlatformManaged ? (
                                        <Dropdown.Item
                                            onClick={() => openDeleteConfirm(role.id, role.name)}
                                        >
                                            <Icon name="mdi-delete" className="mr-2" />
                                            {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : isPlatformManaged ? (
                                        <Tooltip content={i18n.t('roles_system_roles_cannot_be_deleted.text')} placement="top">
                                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-delete" className="mr-2" />
                                                    {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <ConditionalNav />

            <HelpTextBlock helpText={`${i18n.t('roles_help_text.text')}`} />

            {!isModuleEnabled(userData, 'addon-advanced-user-roles') && (
                <div className="alert alert-warning">
                    {i18n.t('roles_advanced_user_roles_module_not_activated.text')}
                </div>
            )}


            <Row>
                <Col lg={3}>
                    <SearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={i18n.t('roles_search_placeholder.text')}
                    />
                </Col>
                <Col lg={9} className="d-flex justify-content-end gap-2">
                    {canCompareRoles && (
                        <div style={{marginRight: '12px'}}>
                            <Button
                                variant="outline-info"
                                size="sm"
                                className="mb-2"
                                icon="mdi-compare"
                                onClick={() => openComparisonModal()}
                                data-cy="compare-roles-btn"
                            >
                                {i18n.t('roles_compare_roles.text')}
                            </Button>
                        </div>
                    )}
                    {canCreateRoles && (
                        <Button
                            variant="primary"
                            size="sm"
                            className="mb-2"
                            icon="mdi-plus"
                            onClick={() => openRoleModal()}
                            data-cy="create-role-btn"
                        >
                            {i18n.t('add_new_role.text')}
                        </Button>
                    )}
                </Col>
            </Row>

            {loadingRoles ? (
                <Spinner text={i18n.t('sp_loading.text')} centered />
            ) : (
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={filteredRoles as Role[]} columns={columns} />
                    </div>
                </div>
            )}

            {roleModalOpen && (
                <RoleModal
                    modalOpen={roleModalOpen}
                    closeModal={closeRoleModal}
                    activeRole={selectedRole}
                />
            )}

            {roleDetailModalOpen && selectedRole && (
                <RoleDetailModal
                    modalOpen={roleDetailModalOpen}
                    closeModal={closeRoleDetailModal}
                    role={selectedRole}
                />
            )}

            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('roles_delete_role.text')}
                message={
                    roleToDelete
                        ? i18n.t('roles_delete_confirmation_with_name.text', {roleName: roleToDelete.name})
                        : i18n.t('roles_delete_confirmation_generic.text')
                }
                actions={[
                    {
                        label: i18n.t('roles_delete_role.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting,
                    }
                ]}
                dismissible={!isDeleting}
            />

            <RoleCloneModal
                isOpen={cloneModalOpen}
                onClose={closeCloneModal}
                onConfirm={confirmClone}
                sourceRole={roleToClone}
                isLoading={isCloning}
            />

            <RoleComparisonModal
                modalOpen={comparisonModalOpen}
                closeModal={closeComparisonModal}
                availableRoles={allRoles}
                preselectedRole={preselectedRoleForComparison}
            />
        </>
    );
});

export default RolesListView;
